/**
 * The culture system's rules, as plain functions (Multilang_Culture_Spec.md 5): which language a URL is in, how a path is
 * moved into another language, which app-key set a page loads, and how a label is looked up. No Angular here, so the
 * server render, the browser and the tests all run the same code. This folder is the same file for file in every host's
 * app (no brand words): the host's languages and app keys come from its environment.
 */

export interface CultureLanguage {
    code: string;
    nativeName: string;
    direction: 'ltr' | 'rtl';
    indexable: boolean;
}

/** A dictionary file as the server publishes it (3.1): {lang}_{labelid} to text. */
export interface CultureDictionary {
    [key: string]: string;
}

export const ENGLISH = 'en';

/**
 * The visitor's chosen language (5.6): every address without a language prefix is shown in it, and emails and SMS go out
 * in it. Set only by the language switch and the suggestion.
 */
export const CULTURE_COOKIE = 'culture';

/** The languages a visitor turned the suggestion banner down for (5.6). */
export const SUGGEST_DISMISSED_COOKIE = 'culture_suggest_no';

/** The longest a page waits for its dictionary before it draws in English (5.1). */
export const DICTIONARY_TIME_LIMIT_MS = 3000;

/** Angular's locale for each language (5.4). Arabic is ar-AE: Angular prints 0-9 in every locale, and ar-AE's separators are . and , - prices read like the Gulf shops'. */
export const LOCALE_IDS: { [lang: string]: string } = { en: 'en-US', ar: 'ar-AE', es: 'es', bn: 'bn' };

/** What a language is called in itself, its direction, and the line that offers it - used when the server has said nothing else. */
export const BUILT_IN_LANGUAGES: { [lang: string]: CultureLanguage } = {
    en: { code: 'en', nativeName: 'English', direction: 'ltr', indexable: true },
    ar: { code: 'ar', nativeName: 'العربية', direction: 'rtl', indexable: false },
    es: { code: 'es', nativeName: 'Español', direction: 'ltr', indexable: false },
    bn: { code: 'bn', nativeName: 'বাংলা', direction: 'ltr', indexable: false },
};

/** The suggestion banner's words, in the language it suggests (5.6): a visitor who reads it is the one it is for. */
export const SUGGESTIONS: { [lang: string]: { ask: string, yes: string, no: string } } = {
    en: { ask: 'View this page in English?', yes: 'English', no: 'No, thanks' },
    ar: { ask: 'هل تريد عرض هذه الصفحة بالعربية؟', yes: 'العربية', no: 'لا، شكرًا' },
    es: { ask: '¿Ver esta página en español?', yes: 'Español', no: 'No, gracias' },
    bn: { ask: 'এই পেজটি বাংলায় দেখুন?', yes: 'বাংলা', no: 'না, ধন্যবাদ' },
};

/** The language most visitors from a country read, for the banner only - never to switch a page (5.6). */
export const COUNTRY_LANGUAGES: { [country: string]: string } = {
    AE: 'ar', SA: 'ar', QA: 'ar', KW: 'ar', BH: 'ar', OM: 'ar', EG: 'ar', JO: 'ar', LB: 'ar', IQ: 'ar', MA: 'ar', DZ: 'ar',
    TN: 'ar', LY: 'ar', SD: 'ar', YE: 'ar', SY: 'ar', PS: 'ar', MR: 'ar',
    ES: 'es', MX: 'es', AR: 'es', CO: 'es', CL: 'es', PE: 'es', VE: 'es', EC: 'es', GT: 'es', CU: 'es', BO: 'es', DO: 'es',
    HN: 'es', PY: 'es', SV: 'es', NI: 'es', CR: 'es', PA: 'es', UY: 'es', PR: 'es',
    BD: 'bn',
};

export function languageInfo(code: string, published?: CultureLanguage[]): CultureLanguage {
    const found = (published || []).filter(l => l && l.code === code)[0];
    return found || BUILT_IN_LANGUAGES[code] || { code, nativeName: code, direction: 'ltr', indexable: code === ENGLISH };
}

/**
 * A page's language (the owner, 2026-10-17: a chosen language stays, with no prefix in the address): the address's prefix
 * when it has one (/bn/blog/x), else the visitor's choice (the culture cookie) when the host has that language, else
 * English. A crawler carries no cookie, so every address without a prefix is English to it.
 */
export function pageLanguage(path: string, cookies: string, languages: readonly string[]): string {
    const prefixed = langOfPath(path, languages);
    if (prefixed !== ENGLISH) { return prefixed; }
    const chosen = cookieValue(cookies, CULTURE_COOKIE).toLowerCase();
    return chosen && (languages || []).indexOf(chosen) >= 0 ? chosen : ENGLISH;
}

/** The language a path's prefix names: its first segment when that is one of the host's other languages, else English. */
export function langOfPath(path: string, languages: readonly string[]): string {
    const m = /^\/([^\/?#]+)/.exec(String(path || ''));
    const first = m ? m[1].toLowerCase() : '';
    return first !== ENGLISH && (languages || []).indexOf(first) >= 0 ? first : ENGLISH;
}

/** The path without its language prefix: /ar/account/orders is /account/orders, /ar is /, /ar?x=1 is /?x=1. */
export function pathWithoutLang(path: string, languages: readonly string[]): string {
    const p = String(path || '/');
    const lang = langOfPath(p, languages);
    if (lang === ENGLISH) { return p; }
    const rest = p.substring(lang.length + 1);
    return rest === '' || rest[0] !== '/' ? '/' + rest : rest;
}

/** An app path in a language: '/account/cart' in Arabic is '/ar/account/cart'; '/' is '/ar'. Paths that are not the app's own (relative, external, a fragment) are left alone. */
export function pathInLang(path: string, lang: string, languages: readonly string[]): string {
    const p = String(path == null ? '' : path);
    if (p[0] !== '/' || p.indexOf('//') === 0) { return p; }
    const bare = pathWithoutLang(p, languages);
    if (!lang || lang === ENGLISH) { return bare; }
    const rest = bare === '/' ? '' : bare.indexOf('/?') === 0 || bare.indexOf('/#') === 0 ? bare.substring(1) : bare;
    return '/' + lang + rest;
}

/** Whether a path is the account area, whose pages load the dashboard's set (core, the host's, seller). */
export function isAccountPath(path: string, languages: readonly string[]): boolean {
    const bare = pathWithoutLang(String(path || '/'), languages).split(/[?#]/)[0];
    return bare === '/account' || bare.indexOf('/account/') === 0;
}

/**
 * The account area's shopper pages - the cart, checkout, payment and sign-in - draw with the storefront's labels: a shopper
 * on the way to paying never waits for the dashboard's (five times the size).
 */
export const SHOPPER_ACCOUNT_PAGES: readonly string[] = [
    '/account/cart', '/account/checkout', '/account/place-order', '/account/login', '/account/signup', '/account/password',
    '/account/reset-password',
];

/** Whether a page loads the dashboard's set (core, the host's, seller): the account area, but for its shopper pages. */
export function usesSellerSet(path: string, languages: readonly string[]): boolean {
    if (!isAccountPath(path, languages)) { return false; }
    const bare = pathWithoutLang(String(path || '/'), languages).split(/[?#]/)[0];
    return !SHOPPER_ACCOUNT_PAGES.some(page => bare === page || bare.indexOf(page + '/') === 0);
}

/** The app keys a page loads, in order (2.2): core, the host's, then seller on the dashboard's pages (usesSellerSet). */
export function setKeys(appKeys: readonly string[], seller: boolean): string[] {
    const keys = (appKeys && appKeys.length ? appKeys : ['core']).slice();
    if (seller && keys.indexOf('seller') < 0) { keys.push('seller'); }
    return keys;
}

/** The manifest's key for a language's set: "ar|core,izz". */
export function fileKey(lang: string, keys: readonly string[]): string {
    return lang + '|' + keys.join(',');
}

/**
 * A category's label key, from its English name as the shop stores it: "Car & Bike" is cat-car-bike. The menu data files
 * carry the same keys, so a category reads the same in the menu, on its page and in a breadcrumb.
 */
export function categoryKey(name: string): string {
    return 'cat-' + String(name || '').toLowerCase().replace(/&/g, ' ').replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
}

/** The {name} placeholders of a text, sorted. */
export function placeholders(text: string): string[] {
    const found: { [name: string]: boolean } = {};
    String(text || '').replace(/\{([A-Za-z0-9_]+)\}/g, (_: string, name: string) => { found[name] = true; return ''; });
    return Object.keys(found).sort();
}

export function samePlaceholders(a: string, b: string): boolean {
    return placeholders(a).join('\u0000') === placeholders(b).join('\u0000');
}

/** {name} filled from params; a name with no value stays as written. */
export function formatLabel(text: string, params?: { [name: string]: any }): string {
    if (!params) { return text; }
    return String(text).replace(/\{([A-Za-z0-9_]+)\}/g, (whole: string, name: string) =>
        params[name] === undefined || params[name] === null ? whole : String(params[name]));
}

/**
 * A label (5.1, 12.4): the dictionary's text for {lang}_{key}, else the English written inline. A text whose placeholders
 * are not the English's is passed over for the English (onMismatch is told, to log it once). Never the bare key.
 */
export function translate(dict: CultureDictionary, lang: string, key: string, fallback: string,
                          params?: { [name: string]: any }, onMismatch?: (dictKey: string) => void): string {
    const english = fallback == null ? '' : String(fallback);
    let text = english;
    if (key && dict) {
        const dictKey = lang + '_' + String(key).toLowerCase();
        const found = dict[dictKey];
        if (found != null && found !== '') {
            if (samePlaceholders(found, english)) {
                text = found;
            } else if (onMismatch) {
                onMismatch(dictKey);
            }
        }
    }
    return formatLabel(text, params);
}

/** A cookie's value from a Cookie header or document.cookie, or ''. */
export function cookieValue(cookies: string, name: string): string {
    const m = new RegExp('(?:^|;\\s*)' + name + '=([^;]*)').exec(String(cookies || ''));
    try { return m ? decodeURIComponent(m[1]).trim() : ''; } catch { return ''; }
}

/**
 * The language to offer a visitor, or null (5.6): their browser's first language when the host has it and it is not
 * English; else, on an English page, their country's (the owner, 2026-10-22: most phones in Bangladesh are set to English,
 * so a visitor from Bangladesh is offered Bangla once); on a page in another language, English to an English browser.
 * Never the page's own, never one they turned down.
 */
export function suggestedLanguage(pageLang: string, languages: readonly string[], browserLanguages: readonly string[],
                                  country: string, dismissed: readonly string[]): string {
    const first = String((browserLanguages || [])[0] || '').toLowerCase().split('-')[0];
    const byCountry = COUNTRY_LANGUAGES[String(country || '').toUpperCase()] || null;
    const known = languages.indexOf(first) >= 0;
    const wanted = known && first !== ENGLISH ? first
        : known && pageLang !== ENGLISH ? ENGLISH
        : byCountry;
    if (!wanted || wanted === pageLang || languages.indexOf(wanted) < 0 || (dismissed || []).indexOf(wanted) >= 0) { return null; }
    return wanted;
}
